/** date related utility functions */
import { displayToUtcDateString } from '../redux/modules/timezones';
import { defaultMemoize } from 'reselect';

/**
 * returns a new utc date adjusted as per event timezone from event date
 * @param dateString
 * @param eventTimezone
 * @returns {Date}
 */
export function getUtcDateFromEventDateString(dateString, eventTimezone) {
  const date = new Date(dateString);
  const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return displayToUtcDateString(adjustedDate, eventTimezone);
}

/**
 * returns a new date adjusted as per local timezone from event date string
 * @param dateString
 * @returns {Date}
 */
export function getLocalDateFromEventDateString(dateString) {
  const date = new Date(dateString);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

/**
 * returns a list of resx for months to be used in PickADate component
 * @param translate
 * @returns {Array}
 */
export const getMonths = defaultMemoize((translate) => {
  return [
    translate('HotelRequest_label_month_jan'),
    translate('HotelRequest_label_month_feb'),
    translate('HotelRequest_label_month_mar'),
    translate('HotelRequest_label_month_apr'),
    translate('HotelRequest_label_month_may'),
    translate('HotelRequest_label_month_jun'),
    translate('HotelRequest_label_month_jul'),
    translate('HotelRequest_label_month_aug'),
    translate('HotelRequest_label_month_sep'),
    translate('HotelRequest_label_month_oct'),
    translate('HotelRequest_label_month_nov'),
    translate('HotelRequest_label_month_dec')
  ];
});

/**
 * returns list of resx for weeks to be used in PickADate component
 * @param translate
 * @returns {Array}
 */
export const getWeekDaysShort = defaultMemoize((translate) => {
  return [
    translate('HotelRequest_label_day_sun'),
    translate('HotelRequest_label_day_mon'),
    translate('HotelRequest_label_day_tue'),
    translate('HotelRequest_label_day_wed'),
    translate('HotelRequest_label_day_thu'),
    translate('HotelRequest_label_day_fri'),
    translate('HotelRequest_label_day_sat')
  ];
});

/**
 * Function to add 0 as prefix when value is less than 10.
 * @param {number} number
 * @returns {string}
 */
const padZero = (number) => number < 10 ? `0${number}` : `${number}`;

/**
 * returns time in format of 'HH:MM:SS AM/PM'
 * @param {Object} timeObj time in format of { hour: 12, minute: 12, second: 00 }
 * @returns {string}
 */
export function getTimeStringFromNucleusTime(timeObj) {
  if (!timeObj) return null;
  let { hour = 0, minute = 0, second = 0 } = timeObj;
  let amPm = hour >= 12 ? 'PM' : 'AM';
  if (hour > 12) {
    hour = hour % 12;
  }
  return `${padZero(hour)}:${padZero(minute)}:${padZero(second)} ${amPm}`;
}


const appendAmPmToTimeString = (timeString) => {
  const timeArr = timeString.split(':');
  let hour = parseInt(timeArr[0] || 0, 10);
  let minute = parseInt(timeArr[1] || 0, 10);
  let second = parseInt(timeArr[2] || 0, 10);

  let amPm = hour >= 12 ? 'PM' : 'AM';
  if (hour > 12) {
    hour = hour % 12;
  }
  return `${padZero(hour)}:${padZero(minute)}:${padZero(second)} ${amPm}`;
};

/**
 * Splits a date string in the format of "2020-08-19T14:00:00.000Z"
 * into 2020-08-19 & 02:00:00 PM
 */
export const getDateAndTimeFromIsoDateString = defaultMemoize((isoDateString) => {
  const split = isoDateString && isoDateString.split('T');
  if (!split || split.length !== 2) {
    return { dateString: '', timeString: '' };
  }
  const timeString = (split[1].indexOf('.') !== -1) && split[1].substr(0, split[1].indexOf('.'));
  return {
    dateString: split[0],
    timeString: (timeString && appendAmPmToTimeString(timeString)) || ''
  };
});

/**
 * returns nucleus time picker object in format of { hour: 12, minute: 12, second: 00 }
 * @param {string} timeString time in format of 'HH:MM:SS AM/PM'
 * @returns {Object}
 */
export function getNucleusTimeFromTimeString(timeString) {
  if (!timeString) return null;

  const fullTimeArr = timeString.split(' ');
  const amPm = fullTimeArr[1];
  const timeArr = fullTimeArr[0].split(':');
  let hour = parseInt(timeArr[0] || 0, 10);
  let minute = parseInt(timeArr[1] || 0, 10);
  let second = parseInt(timeArr[2] || 0, 10);

  if (amPm === 'PM' && hour !== 12) {
    hour += 12;
  } else if (amPm === 'AM' && hour === 12) {
    hour = 0;
  }
  return { hour, minute, second };
}

/**
 * Returns date only string in format 'YYYY-MM-DD'
 * @param {Object} dateObj
 * @returns {string}
 */
export function getDateOnlyString(dateObj) {
  if (!dateObj || typeof dateObj !== 'object') return dateObj;

  const year = dateObj.getFullYear();
  let month = dateObj.getMonth() + 1;
  let date = dateObj.getDate();

  return `${year}-${padZero(month)}-${padZero(date)}`;
}

/**
 * Returns display time for UI from 'HH:MM:SS AM/PM' format
 * @param {string} timeString time in format of 'HH:MM:SS AM/PM'
 * @param {Object} eventTimezone
 * @param {Function} translateTime
 * @returns {string}
 */
export function displayTimeFromTimeString(timeString, eventTimezone, translateTime) {
  if (!timeString || !eventTimezone || !translateTime) {
    return null;
  }
  let dObj = getNucleusTimeFromTimeString(timeString);
  let date = new Date();
  date.setHours(dObj.hour);
  date.setMinutes(dObj.minute);
  date.setSeconds(dObj.second);
  return translateTime(displayToUtcDateString(date, eventTimezone));
}
